import { type PaletteColor, useTheme } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ANNIE_STUDIO } from "../../../theme/tokens";
import { AssistantHumanAccessory, type AssistantHumanAvatar } from "../../UIToolkit/AssistantAvatar/assistantAvatarConfig";
import { HAIR_BY_COLOR, SKIN_BY_TONE } from "../../UIToolkit/AssistantAvatar/assistantAvatarPalette";
import { VisualChoiceShape } from "../../UIToolkit/Clickable/VisualChoiceGrid";
import { type AnnieStudioPartRow, AnnieStudioPartSections, partRow } from "./AnnieStudioPartSections";
import { bodyColorSwatch, pictureOptions, swatchOptions } from "./annieStudioOptions";
import { HUMAN_SLOT_VALUES, type HumanSlot } from "./annieStudioSlots";

type OnHumanPartChange = <Slot extends HumanSlot>(slot: Slot, value: AssistantHumanAvatar[Slot]) => void;

type Props = {
	draft: AssistantHumanAvatar;
	onChange: OnHumanPartChange;
	disabled: boolean;
};

const humanRows = (t: TFunction, draft: AssistantHumanAvatar, onChange: OnHumanPartChange, disabled: boolean, primary: PaletteColor): AnnieStudioPartRow[] => {
	const bare = { ...draft, accessory: AssistantHumanAccessory.None };
	const picture = <Slot extends HumanSlot>(slot: Slot, titleKey: string, size: number, base: AssistantHumanAvatar) =>
		partRow({
			slot,
			title: t(titleKey),
			value: draft[slot],
			options: pictureOptions(t, slot, HUMAN_SLOT_VALUES[slot], (value) => ({ ...base, [slot]: value }), size),
			onChange: (value) => onChange(slot, value),
			shape: VisualChoiceShape.Rounded,
			disabled,
		});
	const swatch = <Slot extends HumanSlot>(slot: Slot, titleKey: string, background: (value: AssistantHumanAvatar[Slot]) => string) =>
		partRow({
			slot,
			title: t(titleKey),
			value: draft[slot],
			options: swatchOptions(t, slot, HUMAN_SLOT_VALUES[slot], background),
			onChange: (value) => onChange(slot, value),
			shape: VisualChoiceShape.Circle,
			disabled,
		});
	return [
		swatch("skin_tone", "annie.studio.slot.skin_tone", (tone) => SKIN_BY_TONE[tone]),
		picture("hair", "annie.studio.slot.hair", ANNIE_STUDIO.FACE_AVATAR, bare),
		swatch("hair_color", "annie.studio.slot.hair_color", (color) => HAIR_BY_COLOR[color]),
		picture("facial_hair", "annie.studio.slot.facial_hair", ANNIE_STUDIO.FACE_AVATAR, bare),
		swatch("color", "annie.studio.slot.scrubs", (color) => bodyColorSwatch(color, primary)),
		picture("eyes", "annie.studio.slot.eyes", ANNIE_STUDIO.FACE_AVATAR, bare),
		picture("accessory", "annie.studio.slot.extras", ANNIE_STUDIO.PART_AVATAR, draft),
	];
};

/** Skin tone, hair, facial hair, scrubs, eyes and extras for the human style. */
export const AnnieStudioHumanParts: FC<Props> = ({ draft, onChange, disabled }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const rows = useMemo(() => humanRows(t, draft, onChange, disabled, theme.palette.primary), [t, draft, onChange, disabled, theme.palette.primary]);
	return <AnnieStudioPartSections rows={rows} />;
};
